import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Bell, Send, CheckCircle2, Lock } from 'lucide-react';
import SiteFooter from '../components/sections/SiteFooter';
import SubmitTipButton from '../components/SubmitTipButton';
import TopNav from '../components/sections/TopNav';
import PageSeoTag from '../components/PageSeoTag';

const API = process.env.REACT_APP_BACKEND_URL;
const HERO_ART = 'https://customer-assets.emergentagent.com/job_d497ffe6-f7ac-4d46-ab2f-f65a2fd380b7/artifacts/8jk2o059_1000023626.png';

const Merch = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source: 'merch_drop_alerts' }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Sign-up failed.');
      }
      setSuccess(true);
      setEmail('');
    } catch (err) {
      setError(err.message || 'Sign-up failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="merch-page">
      <TopNav />
      <PageSeoTag pageKey="merch" />

      <header className="bg-zinc-900/80 backdrop-blur-xl border-b border-red-900/30 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-300 hover:text-white text-sm font-bold" data-testid="merch-back-home">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
          <span className="text-sm font-black tracking-widest text-red-400">MERCH</span>
        </div>
      </header>

      {/* Hero with overlaid canvas artwork */}
      <section
        className="relative w-full flex items-center justify-center py-10 px-4"
        data-testid="merch-hero"
      >
        <img
          src={HERO_ART}
          alt="Outlaw Media gallery canvas - Pay the Tax"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
          data-testid="merch-hero-art"
        />
        {/* Dark vignette + bottom fade for legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black"></div>
        <div className="absolute inset-0 bg-radial-vignette"></div>

        <div className="relative max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-400/10 border border-yellow-500/40 backdrop-blur-md mb-6">
            <Lock className="h-3.5 w-3.5 text-yellow-400" />
            <span className="text-[11px] font-black tracking-[0.25em] text-yellow-300 uppercase">
              Coming Soon
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight tracking-tight drop-shadow-[0_4px_24px_rgba(0,0,0,0.9)]">
            OUTLAW <span className="text-red-500">MEDIA</span> MERCH
          </h1>
          <p className="text-base md:text-lg text-gray-100 leading-relaxed max-w-xl mx-auto drop-shadow-[0_2px_12px_rgba(0,0,0,0.9)]">
            Wearable propaganda. Street wear runs we{' '}
            <span className="text-red-400 font-bold">won't restock</span>. One-off, custom
            artworks on archival-grade canvas you'll actually want to frame.
          </p>
          <p className="mt-4 text-base md:text-lg text-white font-black tracking-wide drop-shadow-[0_2px_12px_rgba(0,0,0,0.95)]">
            Outlaw Media merch isn't a side hustle -{' '}
            <span className="text-red-500">it's a rare privilege.</span>
          </p>
        </div>

        <style>{`
          .bg-radial-vignette {
            background: radial-gradient(ellipse at center, transparent 25%, rgba(0,0,0,0.7) 90%);
          }
        `}</style>
      </section>

      {/* Coming Soon products + drop alerts capture */}
      <section
        className="max-w-3xl mx-auto px-4 py-6"
        data-testid="merch-drop-alerts"
      >
        {/* Coming Soon products */}
        <section data-testid="merch-coming-soon">
          <div className="text-center mb-6">
            <p className="text-[11px] tracking-[0.35em] uppercase text-red-500 font-black mb-1">
              First Drops
            </p>
            <h2 className="text-2xl md:text-3xl font-black uppercase tracking-wider">
              Coming <span className="text-red-500">Soon</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            {[
              { id: 'unnamed-tee',   label: 'Tee · Name Pending', sub: 'Limited drop · One-off print run' },
              { id: 'kaz-bobble',    label: 'Kaz Hamad Bobble Head', sub: 'Collectible figure · Numbered edition' },
            ].map((p) => (
              <div
                key={p.id}
                data-testid={`merch-coming-${p.id}`}
                className="relative rounded-2xl overflow-hidden border border-red-900/40 bg-zinc-950/80 group hover:border-red-500/60 transition-colors"
              >
                {/* Mystery placeholder image */}
                <div className="aspect-square relative bg-gradient-to-br from-zinc-900 via-black to-red-950/40 flex items-center justify-center overflow-hidden">
                  {/* Faint scanline overlay */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 opacity-[0.08] pointer-events-none"
                    style={{ backgroundImage: 'repeating-linear-gradient(0deg, #fff 0 1px, transparent 1px 4px)' }}
                  />
                  {/* Faint grid */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 opacity-[0.06] pointer-events-none"
                    style={{
                      backgroundImage:
                        'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
                      backgroundSize: '40px 40px',
                    }}
                  />
                  {/* Giant question mark */}
                  <span
                    aria-hidden="true"
                    className="relative font-black text-red-500 select-none leading-none"
                    style={{
                      fontSize: 'clamp(120px, 38vw, 220px)',
                      textShadow: '0 0 40px rgba(220,38,38,0.65), 0 0 80px rgba(220,38,38,0.35)',
                      filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.7))',
                    }}
                  >
                    ?
                  </span>

                  {/* Coming Soon ribbon */}
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-yellow-400/15 border border-yellow-500/50 backdrop-blur-md">
                    <Lock className="h-3 w-3 text-yellow-300" />
                    <span className="text-[9px] font-black tracking-[0.25em] uppercase text-yellow-300">
                      Coming Soon
                    </span>
                  </span>
                </div>

                <div className="p-4 sm:p-5">
                  <p className="text-[10px] font-black tracking-widest uppercase text-gray-500 mb-1">
                    Outlaw Media · First Drop
                  </p>
                  <h3 className="text-lg sm:text-xl font-black text-white leading-tight mb-2">
                    {p.label}
                  </h3>
                  <p className="text-xs text-gray-400 mb-3">{p.sub}</p>

                  <div className="flex items-center justify-between gap-3">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-zinc-900 border border-zinc-800 text-[10px] font-black uppercase tracking-widest text-gray-500">
                      <Lock className="h-3 w-3" />
                      Price TBA
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-gray-600">
                      Sign up below for the drop alert
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Drop alerts capture - now sits BELOW the products */}
        <div
          className="mt-10 bg-gradient-to-br from-red-950/50 via-black to-black border-2 border-red-700/50 rounded-2xl p-7 md:p-10 text-center shadow-[0_18px_40px_-12px_rgba(220,38,38,0.4)]"
          data-testid="merch-drop-alerts-box"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-600/15 border border-red-500/40 mb-5">
            <Bell className="h-3.5 w-3.5 text-red-400" />
            <span className="text-[11px] font-black tracking-[0.25em] text-red-400 uppercase">
              Drop Alerts
            </span>
          </div>
          <h2 className="text-2xl md:text-4xl font-black text-white mb-3">
            BE THE FIRST <span className="text-red-500">HIT</span>.
          </h2>
          <p className="text-sm md:text-base text-gray-400 mb-6 max-w-lg mx-auto">
            We don't restock. When something drops, it ships, then it's gone. Get the alert
            <span className="text-white font-bold"> before everyone else.</span>
          </p>

          {success ? (
            <div
              className="inline-flex items-center gap-2 bg-emerald-900/30 border border-emerald-500/50 text-emerald-300 px-5 py-3 rounded-lg font-bold"
              data-testid="merch-signup-success"
            >
              <CheckCircle2 className="h-5 w-5" />
              Check your inbox and tap the confirm link - that's the last step.
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto"
              data-testid="merch-signup-form"
            >
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="merch-email-input"
                className="flex-1 bg-black/60 border border-red-900/40 text-white px-4 py-3 rounded text-sm focus:outline-none focus:border-red-500"
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="merch-signup-submit"
                className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-black tracking-wide px-5 py-3 rounded transition-colors flex items-center justify-center gap-2"
              >
                <Send className="h-4 w-4" />
                {submitting ? 'SIGNING UP...' : 'NOTIFY ME'}
              </button>
            </form>
          )}
          {error && (
            <p className="text-red-400 text-sm mt-3" data-testid="merch-signup-error">{error}</p>
          )}
          <p className="text-[10px] text-gray-600 mt-4 tracking-wider uppercase">
            One email per drop. No spam. Unsubscribe anytime.
          </p>
        </div>

        {/* Artist collab pitch */}
        <p className="mt-8 text-center text-sm text-gray-500">
          Artist, designer, or photographer with a chip on your shoulder?{' '}
          <a
            href="mailto:ryan@outlawmedia.com.au?subject=Outlaw Media Merch Collab"
            className="text-red-400 hover:text-red-300 font-bold underline"
            data-testid="merch-collab-mailto"
          >
            Pitch us a collab.
          </a>
        </p>
      </section>

      <SiteFooter />
      <SubmitTipButton />
    </div>
  );
};

export default Merch;
